import React, { useContext } from 'react';
import { set, get, filter } from 'lodash';
import styled from 'styled-components';

import { AlertWarning } from '@ad-scripts/presentation/components/common/alert';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import {
  EditorSection,
  OptionTable,
  StyledOptionsSection,
  StyledSectionContainer,
} from 'src/presentation/components/pages/shared/EditorSectionLayout';
import ErrorLabel from '@ad-scripts/presentation/components/common/forms/control_container';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import { FIELDS_MAPPER } from 'src/presentation/components/pages/chat/chatConstants';
import FormContext from 'src/presentation/contexts/FormContext';
import integrationsResource from 'src/actions/integrations/integrationsResource';
import isThreadDependencySatisfied from 'src/presentation/components/pages/threads/helpers/isThreadDependencySatisfied';
import LinkOutStroke from '@ad-scripts/presentation/components/common/icons/stroke/link-out-stroke';
import generateId from 'src/lib/generateId';
import { HalfRow } from 'src/presentation/components/pages/chat/ChatLayout';
import InformationIcon from '@ad-scripts/presentation/components/lib/icons/information_icon';
import Input from 'src/presentation/components/forms/Input';
import useWismoAutomations from 'src/presentation/components/pages/chat/hooks/useWismoAutomations';
import useResource, { allSucceeded } from 'src/presentation/hooks/useResource';
import { CHAT_BRAND } from 'src/constants/branding';

const QuickReplyInputs = ({ errors, resetError }) => {
  const { workingState, updateState } = useContext(FormContext);
  const { loadingState: externalSourcesLoadingState, values: externalSources = [] } = useResource(integrationsResource);
  const isThreadDepSatisfied =
    allSucceeded(externalSourcesLoadingState) && isThreadDependencySatisfied(externalSources);
  const userResponseType: string = get(workingState, FIELDS_MAPPER.ONBOARDING_RESPONSE_TYPE.path, 'TEXT');
  const onboardingEnabled = get(workingState, FIELDS_MAPPER.ONBOARDING_ENABLED.path, false);

  const { isThreadsFeatureActivated, options: threadsOptions } = useWismoAutomations();

  if (userResponseType !== 'QUICK_REPLY_RESPONSE') {
    return null;
  }

  const getNewItemDefaultData = () => {
    return {
      id: generateId(),
      buttonId: '',
      label: '',
    };
  };

  const description = (
    <>
      Set the Quick Reply buttons by assigning the Button Label and ID in the fields below. Make sure to use the correct
      Button ID so the Quick Reply options appear properly in the {CHAT_BRAND.name}.
      {isThreadsFeatureActivated && onboardingEnabled && (
        <>
          <DescriptionInfoContainer>
            <StyledInformationIcon />
            <DescriptionInfoText>
              Please note that there is a fee per resolution when assigning Self-Service Threads to a button. Visit our{' '}
              <ExternalLink to="https://help.gladly.com/docs/what-is-self-service-threads" label="Help Docs" /> to read
              about this feature.
            </DescriptionInfoText>
          </DescriptionInfoContainer>
          {!isThreadDepSatisfied ? (
            <StyledAlertWarning>
              <div>
                <b>Note: </b>
                You must have Shopify, BigCommerce, Magento, or a custom Lookup Adaptor linked to an OMS for customers
                to view their orders. <StyledExternalLink to={'https://help.gladly.com'} label={'Learn why'} />
                <StyledLinkedOut />
              </div>
            </StyledAlertWarning>
          ) : null}
        </>
      )}
    </>
  );

  const renderItem = ({ index }) => {
    const attrPrefix = `${FIELDS_MAPPER.QUICK_REPLY_OPTIONS.path}[${index}]`;
    const attrLabel = `${attrPrefix}.label`;
    const attrId = `${attrPrefix}.buttonId`;
    const attrAutomationId = `${attrPrefix}.automationId`;

    const aidPrefix = `item-row[${index}]`;
    const aidLabel = `${aidPrefix}-label`;
    const aidId = `${aidPrefix}-buttonId`;
    const aidAutomationId = `${attrPrefix}-automationId`;

    return (
      <QuickReplyRow>
        <StyledInput placeholder={'Button Label'} attr={attrLabel} data-aid={aidLabel} />
        <StyledInput placeholder={'Button ID'} attr={attrId} data-aid={aidId} />
        {isThreadsFeatureActivated && onboardingEnabled && (
          <Dropdown
            allowEmptyValues
            attr={attrAutomationId}
            data-aid={aidAutomationId}
            options={threadsOptions}
            placeholder={'No Thread assigned'}
            searchable
          />
        )}
      </QuickReplyRow>
    );
  };

  const onDelete = id => {
    errors
      .filter(error => error.attr.startsWith(`${FIELDS_MAPPER.QUICK_REPLY_OPTIONS.path}`))
      .forEach(error => {
        const attr = error.attr;
        resetError({ attr, errors });
      });

    const items = [...get(workingState, FIELDS_MAPPER.QUICK_REPLY_OPTIONS.path)];
    const onboardingMessages = [...get(workingState, FIELDS_MAPPER.ONBOARDING_MESSAGES.path)];
    onboardingMessages.forEach(message => {
      if (message.subType === 'QUICK_REPLY_REQUEST') {
        message.subTypeContent.options = filter(items, item => item.id != id);
      }
    });
    updateState(set({}, FIELDS_MAPPER.ONBOARDING_MESSAGES.path, onboardingMessages), { replaceArrayValues: true });
  };

  return (
    <HalfRow>
      <StyledEditorSection
        data-aid={FIELDS_MAPPER.QUICK_REPLY_OPTIONS.path}
        addButtonLabel={'Add button'}
        attr={FIELDS_MAPPER.QUICK_REPLY_OPTIONS.path}
        getNewItemDefaultData={getNewItemDefaultData}
        renderItem={renderItem}
        description={description}
        onDelete={onDelete}
        minItems={1}
        maxItems={10}
      />
    </HalfRow>
  );
};

export default QuickReplyInputs;

export const StyledInformationIcon = styled(InformationIcon)`
  cursor: pointer;
  fill: ${p => p.theme.colors.gray600};
  height: 16px;
  margin-right: ${p => p.theme.spacing.small};
  width: 16px;
`;

const DescriptionInfoText = styled.div`
  flex: 1;
`;

const DescriptionInfoContainer = styled.div`
  padding-top: 8px;
  display: flex;

  svg {
    position: relative;
    top: 4px;
    margin-right: 8px;
  }
`;

const StyledEditorSection = styled(EditorSection)`
  min-width: 100%;
  background: none;
  border: none;
  margin-top: -${p => p.theme.spacing.stackLarge};

  ${OptionTable} {
    margin: 14px 0 14px 0;
  }

  ${StyledOptionsSection} {
    background: none;
    border: none;
    margin-bottom: 0px;
    padding-bottom: 0px;

    &:first-child td:first-child {
      border-radius: 0px;
    }
    &:first-child td:last-child {
      border-radius: 0px;
    }
  }

  ${StyledSectionContainer} {
    padding: 0px;
  }
`;

const QuickReplyRow = styled.div`
  display: flex;
  margin-left: -${p => p.theme.spacing.medium}};
  flex-wrap: wrap;
  gap: 8px;

  & > div:nth-child(-n+2) {
    flex: 1;
  }

  & > div:nth-child(3) {
    button {
      width: 80%;
    }
  }

  div {
    flex-basis: 100%;
    height: 100%;
    margin-right: ${p => p.theme.spacing.small};
    margin-left: ${p => p.theme.spacing.small};
    width:100%
  }
`;

const StyledInput = styled(Input)`
  display: inline-block;
  flex-basis: 100%;

  ${ErrorLabel} {
    margin-left: ${p => p.theme.spacing.medium};
  }
`;

const StyledAlertWarning = styled(AlertWarning)`
  color: ${p => p.theme.colors.black};
  margin-top: 8px;
  margin-bottom: 8px;
`;

const StyledExternalLink = styled(ExternalLink)`
  text-decoration: underline;
  color: ${p => p.theme.colors.black};

  &:hover {
    text-decoration: underline;
  }
`;

const StyledLinkedOut = styled(LinkOutStroke)`
  width: 16px;
  height: 16px;
  vertical-align: text-bottom;
  padding-left: 2px;
`;
